import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";

// ── /candidate/help ────────────────────────────────────────────────────────
// Backend: POST /job-posting/api/candidate-portal/faqs/list returns
// FaqDTO[] (id, category, question, answer, displayOrder). Grouped by
// category; each Q is a <details> for native a11y + keyboard expand.

const FALLBACK_FAQS = [
  { id: "f1", category: "INTERVIEW", question: "How does the AI interview work?",
    answer: "You record async video answers. The AI scores your responses on clarity, content, and delivery. A human recruiter reviews before any decision." },
  { id: "f2", category: "INTERVIEW", question: "Can I retake an interview?",
    answer: "Depends on the recruiter's settings for that role. If retakes are allowed, you'll see a Retake option on the interview detail page." },
  { id: "f3", category: "PRIVACY", question: "What happens to my recordings?",
    answer: "They're stored securely and only shared with the hiring team for the role you applied to. You can request deletion from your profile page." },
];

export default function CandidateHelp() {
  const [rows, setRows] = useState([]);
  const [usingFallback, setUsingFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    setUsingFallback(false);
    axiosInstance
      .post("/job-posting/api/candidate-portal/faqs/list", {})
      .then((r) => {
        if (r.data?.success) {
          const list = Array.isArray(r.data.data) ? r.data.data : [];
          if (list.length === 0) {
            setRows(FALLBACK_FAQS);
            setUsingFallback(true);
          } else {
            setRows(list);
          }
        } else {
          setError(r.data?.message || "Couldn't load FAQs");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load FAQs");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Help & support - Ample Hire";
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const acc = {};
    [...rows]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .forEach((f) => {
        const k = (f.category || "GENERAL").toUpperCase();
        (acc[k] = acc[k] || []).push(f);
      });
    return acc;
  }, [rows]);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-vf-base m-0">Help & support</h1>
        <p className="text-sm text-vf-muted mt-1">
          Common questions about the interview process. Still stuck? Email the team below.
        </p>
      </header>

      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="row" count={4} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-subtle rounded-card">
          <EmptyState
            icon={<span aria-hidden="true">❓</span>}
            title="No FAQs yet"
            description="We're still curating starter answers. Reach out to support in the meantime."
          />
        </div>
      ) : (
        <div className="space-y-5">
          {usingFallback && (
            <div role="status" className="text-2xs text-vf-muted">
              Showing starter answers until your workspace publishes its own FAQs.
            </div>
          )}
          {Object.entries(grouped).map(([cat, list]) => (
            <section key={cat} aria-label={`${cat} FAQs`} className="space-y-2">
              <div className="flex items-center gap-2">
                <h2 className="text-2xs uppercase tracking-wide text-vf-muted font-semibold m-0">
                  {cat}
                </h2>
                <span className="text-2xs text-vf-muted">{list.length}</span>
              </div>
              <ul className="list-none p-0 m-0 space-y-2">
                {list.map((f) => (
                  <li key={f.id} className="rounded-card border border-subtle bg-white">
                    {/*  <details>/<summary> gives keyboard expand + screen-reader
                         semantics natively; cheaper than a custom collapsible. */}
                    <details className="group">
                      <summary className="list-none px-4 py-3 cursor-pointer flex items-start gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-vf-primary">
                        <span className="flex-1 min-w-0 text-sm font-semibold text-vf-base">
                          {f.question}
                        </span>
                        <span
                          aria-hidden="true"
                          className="text-vf-muted text-lg leading-none flex-shrink-0 transition-transform group-open:rotate-45"
                        >
                          +
                        </span>
                      </summary>
                      <div className="px-4 pb-4 -mt-1 text-sm text-vf-base whitespace-pre-line leading-relaxed">
                        {f.answer}
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <footer className="text-center text-xs text-vf-muted pt-2">
        Still need help?{" "}
        <a
          href="mailto:support@amplehire.com"
          className="text-vf-primary hover:underline font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary rounded"
        >
          support@amplehire.com
        </a>
      </footer>
    </div>
  );
}
